import { FormHelperText } from '@/components/field';
import mergeSlotProps from '@/helper/mergeSlotProps';
import useForkRef from '@/helper/react/useForkRef';
import { resolveComponentProps } from '@/helper/resolveComponentProps';
import { Input } from '@base-ui/react/input';
import classNames from 'classnames';
import * as React from 'react';

import styles from './TextField.module.scss';
import type { TextFieldProps as TextFieldPropsBase } from './textFieldTypes';

export type TextFieldProps = TextFieldPropsBase & {
  /** CSS color for the floating label “notch” (e.g. `var(--background-display-brand)`). Defaults via theme variable to page background. */
  backgroundColor?: string;
};

type OwnerState = Record<string, unknown>;

const stripMuiRootProps = (props: Record<string, unknown>) => {
  const {
    sx: _sx,
    classes: _classes,
    slots: _slots,
    slotProps: _slotProps,
    component: _component,
    color: _color,
    ...rest
  } = props;
  return rest;
};

export const TextField = React.forwardRef<HTMLDivElement, TextFieldProps>(function TextField(inProps, ref) {
  const {
    autoComplete,
    autoFocus = false,
    children,
    className,
    color: _color = 'primary',
    defaultValue,
    disabled = false,
    error = false,
    FormHelperTextProps: FormHelperTextPropsProp,
    fullWidth = false,
    helperText,
    id: idOverride,
    InputLabelProps: InputLabelPropsProp,
    inputProps: inputPropsProp,
    InputProps: InputPropsProp,
    inputAreaRef,
    inputRef,
    label,
    backgroundColor,
    maxRows: _maxRows,
    minRows: _minRows,
    multiline = false,
    name,
    onBlur,
    onChange,
    onFocus,
    placeholder,
    required = false,
    rows,
    select = false,
    SelectProps: _SelectPropsProp,
    size = 'medium',
    slots = {},
    slotProps: slotPropsProp = {},
    type,
    value,
    variant = 'outlined',
    style: rootStyleProp,
    ...otherIn
  } = inProps;

  const other = stripMuiRootProps(otherIn as Record<string, unknown>) as typeof otherIn;

  const ownerState: OwnerState = {
    ...inProps,
    disabled,
    error,
    fullWidth,
    multiline,
    required,
    variant,
  };

  const resolvedSlotProps = {
    input: resolveComponentProps(slotPropsProp.input, ownerState),
    inputLabel: resolveComponentProps(slotPropsProp.inputLabel, ownerState),
    htmlInput: resolveComponentProps(slotPropsProp.htmlInput, ownerState),
    formHelperText: resolveComponentProps(slotPropsProp.formHelperText, ownerState),
  };

  const { props: mergedInputProps } = mergeSlotProps({
    additionalProps:
      variant === 'outlined'
        ? {
            label,
            notched:
              resolvedSlotProps.inputLabel &&
              typeof resolvedSlotProps.inputLabel === 'object' &&
              resolvedSlotProps.inputLabel !== null &&
              'shrink' in resolvedSlotProps.inputLabel
                ? Boolean((resolvedSlotProps.inputLabel as { shrink?: boolean }).shrink)
                : true,
          }
        : {},
    externalForwardedProps: InputPropsProp as Record<string, unknown> | undefined,
    externalSlotProps: resolvedSlotProps.input as Record<string, unknown> | undefined,
  });

  const { props: mergedHtmlInputProps, internalRef: htmlInputInternalRef } = mergeSlotProps({
    externalForwardedProps: mergeSlotProps({
      externalForwardedProps: (InputPropsProp?.inputProps as Record<string, unknown> | undefined) ?? {},
      externalSlotProps: (inputPropsProp as Record<string, unknown> | undefined) ?? {},
    }).props,
    externalSlotProps: (resolvedSlotProps.htmlInput as Record<string, unknown> | undefined) ?? {},
  });

  const { props: mergedInputLabelProps } = mergeSlotProps({
    externalForwardedProps: InputLabelPropsProp as Record<string, unknown> | undefined,
    externalSlotProps: resolvedSlotProps.inputLabel as Record<string, unknown> | undefined,
  });

  const { props: mergedFormHelperTextProps } = mergeSlotProps({
    externalForwardedProps: FormHelperTextPropsProp as Record<string, unknown> | undefined,
    externalSlotProps: resolvedSlotProps.formHelperText as Record<string, unknown> | undefined,
  });

  const reactId = React.useId();
  const id = idOverride ?? reactId;
  const helperTextId = helperText != null && helperText !== false && id ? `${id}-helper-text` : undefined;
  const inputLabelId = label != null && label !== '' && id ? `${id}-label` : undefined;

  const {
    startAdornment,
    endAdornment,
    readOnly,
    className: inputWrapperClassName,
    ref: inputWrapperRef,
    style: inputWrapperStyle,
    sx: _sxInput,
    inputProps: _nestedInputPropsFromMerged,
    ...restMergedInput
  } = mergedInputProps as Record<string, unknown> & {
    startAdornment?: React.ReactNode;
    endAdornment?: React.ReactNode;
    readOnly?: boolean;
    className?: string;
    ref?: React.Ref<HTMLDivElement>;
    style?: React.CSSProperties;
    inputProps?: Record<string, unknown>;
  };

  const forkedInputOutlineRef = useForkRef(inputWrapperRef, inputAreaRef);

  const inputOnlyKeys = new Set([
    'defaultValue',
    'value',
    'id',
    'name',
    'placeholder',
    'autoFocus',
    'autoComplete',
    'type',
    'min',
    'max',
    'step',
    'pattern',
    'maxLength',
    'minLength',
    'inputMode',
    'tabIndex',
    'form',
    'list',
    'multiple',
    'accept',
    'capture',
    'checked',
    'alt',
    'height',
    'width',
    'src',
    'htmlFor',
  ]);

  const wrapperDomBlacklist = new Set(['label', 'notched', 'notchedOutline']);

  const restForWrapper: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(restMergedInput)) {
    if (!inputOnlyKeys.has(key) && key !== 'children' && !wrapperDomBlacklist.has(key)) {
      restForWrapper[key] = val;
    }
  }

  const mergedHtml = mergedHtmlInputProps as Record<string, unknown> & {
    ref?: React.Ref<HTMLInputElement>;
    className?: string;
    style?: React.CSSProperties;
    onChange?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
    onFocus?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
    onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  };

  const {
    ref: htmlRefFromProps,
    className: htmlInputClassName,
    style: htmlInputStyle,
    onChange: onChangeMerged,
    onFocus: onFocusMerged,
    onBlur: onBlurMerged,
    ...htmlInputRest
  } = mergedHtml;

  const forkedInputRef = useForkRef(inputRef, htmlRefFromProps, htmlInputInternalRef);

  const [focused, setFocused] = React.useState(false);

  const labelProps = mergedInputLabelProps as Record<string, unknown>;
  const {
    className: labelClassName,
    style: labelStyle,
    htmlFor: labelHtmlFor,
    id: labelIdProp,
    shrink: shrinkProp,
    sx: _sxLabel,
    ...labelRest
  } = labelProps;

  const isControlled = value !== undefined;
  const [uncontrolledHasValue, setUncontrolledHasValue] = React.useState(
    () => defaultValue != null && String(defaultValue).length > 0
  );
  React.useEffect(() => {
    if (isControlled) return;
    if (defaultValue != null && String(defaultValue).length > 0) {
      setUncontrolledHasValue(true);
    }
  }, [isControlled, defaultValue]);

  const filled = isControlled ? value != null && String(value).length > 0 : uncontrolledHasValue;

  const labelShrink =
    label == null || label === '' ? false : typeof shrinkProp === 'boolean' ? shrinkProp : focused || filled;

  const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFocused(true);
    onFocusMerged?.(e);
    onFocus?.(e as React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFocused(false);
    onBlurMerged?.(e);
    onBlur?.(e as React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (!isControlled) {
      setUncontrolledHasValue(e.target.value.length > 0);
    }
    onChangeMerged?.(e);
    onChange?.(e as React.ChangeEvent<HTMLInputElement>);
  };

  const readOnlyFlag = Boolean(readOnly);
  const disabledFlag = Boolean(disabled);

  const dataAttrs = {
    'data-swd-textfield': '',
    'data-variant': variant,
    'data-size': size,
    'data-error': error ? 'true' : 'false',
    'data-disabled': disabledFlag ? 'true' : 'false',
    'data-focused': focused ? 'true' : 'false',
    'data-readonly': readOnlyFlag ? 'true' : 'false',
    'data-multiline': multiline ? 'true' : 'false',
    'data-full-width': fullWidth ? 'true' : 'false',
  } as const;

  const inputCommon: Record<string, unknown> = {
    ...htmlInputRest,
    'aria-describedby': helperTextId ?? htmlInputRest['aria-describedby'],
    'aria-invalid': error ? true : htmlInputRest['aria-invalid'],
    'data-slot': 'input',
    autoComplete,
    autoFocus,
    defaultValue,
    disabled: disabledFlag,
    id,
    name,
    onBlur: handleBlur,
    onChange: handleChange,
    onFocus: handleFocus,
    placeholder,
    readOnly: readOnlyFlag,
    required,
    value,
    className: classNames(htmlInputClassName),
    style: htmlInputStyle,
    ref: forkedInputRef,
    type: multiline ? undefined : (type ?? 'text'),
  };

  const control = multiline ? (
    <textarea {...inputCommon} rows={rows !== undefined ? Number(rows) : undefined} />
  ) : (
    <Input {...inputCommon} />
  );

  const helperProps = mergedFormHelperTextProps as Record<string, unknown>;
  const {
    sx: _sxFh,
    className: helperClassName,
    error: helperErrorProp,
    component: _helperComponent,
    ...helperRest
  } = helperProps;

  if (select) {
    if (process.env.NODE_ENV !== 'production') {
      console.error(
        'SWD TextField: `select` is not supported. Use MUI Select + FormControl + InputLabel, or refactor the call site.'
      );
    }
    return null;
  }

  const rootStyle: React.CSSProperties | undefined =
    rootStyleProp != null || backgroundColor != null
      ? {
          ...(rootStyleProp as React.CSSProperties | undefined),
          ...(backgroundColor != null ? ({ '--swd-textfield-label-bg': backgroundColor } as React.CSSProperties) : {}),
        }
      : undefined;

  return (
    <div
      ref={ref}
      {...dataAttrs}
      className={classNames(styles.textField, className)}
      {...(other as React.HTMLAttributes<HTMLDivElement>)}
      style={rootStyle}
    >
      {label != null && label !== '' && (
        <label
          {...(labelRest as React.LabelHTMLAttributes<HTMLLabelElement>)}
          className={classNames(labelClassName as string | undefined)}
          data-disabled={disabledFlag ? 'true' : undefined}
          data-shrink={true}
          data-slot="label"
          htmlFor={typeof labelHtmlFor === 'string' ? labelHtmlFor : id}
          id={typeof labelIdProp === 'string' ? labelIdProp : inputLabelId}
          style={labelStyle as React.CSSProperties | undefined}
        >
          {label}
          {required ? '\u2009*' : ''}
        </label>
      )}

      <div
        {...restForWrapper}
        className={classNames(inputWrapperClassName)}
        data-disabled={disabledFlag ? 'true' : undefined}
        data-error={error ? 'true' : 'false'}
        data-has-end-adornment={endAdornment ? 'true' : undefined}
        data-has-start-adornment={startAdornment ? 'true' : undefined}
        data-readonly={readOnlyFlag ? 'true' : 'false'}
        data-slot="outlined-root"
        ref={forkedInputOutlineRef}
        style={inputWrapperStyle}
      >
        <div aria-hidden data-slot="notched-outline" />
        <div data-multiline={multiline ? 'true' : 'false'} data-slot="input-row">
          {startAdornment}
          {control}
          <div data-slot="end-adornment">{endAdornment}</div>
        </div>
      </div>

      {helperText != null &&
        helperText !== false &&
        (error || helperErrorProp ? (
          <FormHelperText
            {...(helperRest as React.HTMLAttributes<HTMLDivElement>)}
            aria-label="Fehlermeldung, ungültige Eingabe im Formular"
            className={classNames(styles.helperText, helperClassName as string | undefined)}
            data-error="true"
            data-slot="helper-text"
            id={helperTextId}
          >
            {helperText}
          </FormHelperText>
        ) : (
          <p
            {...(helperRest as React.HTMLAttributes<HTMLParagraphElement>)}
            className={classNames(styles.helperText, helperClassName as string | undefined)}
            data-error="false"
            data-slot="helper-text"
            id={helperTextId}
          >
            {helperText}
          </p>
        ))}
    </div>
  );
});

TextField.displayName = 'TextField';
